import React from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Bell } from "lucide-react";
import {
  fetchNotifications,
  markNotificationsRead,
  formatRelativeTime,
  type NotificationItem,
} from "../api/social";

// ─── NotificationsBell ────────────────────────────────────────────────────────
// Sidebar bell (mounted in AppShell above the user card, near the bottom of
// the viewport). The `.atlas-glass` dropdown is rendered via a portal to
// <body> with `position: fixed`, anchored to the button's bottom edge so it
// opens upward instead of running off the bottom of the screen.

export function NotificationsBell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState(false);
  const [coords, setCoords] = React.useState<{ bottom: number; left: number } | null>(null);

  const notificationsQuery = useQuery({
    queryKey: ["notifications"],
    queryFn: () => fetchNotifications(),
    refetchInterval: 60_000,
    retry: false,
  });

  const readMutation = useMutation({
    mutationFn: () => markNotificationsRead(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const items = notificationsQuery.data?.items ?? [];
  const unreadCount = notificationsQuery.data?.unreadCount ?? 0;

  // Ring once (a short swing + gold pulse) whenever the unread count goes up,
  // including the first load with something unread. The steady state is the
  // gold bell + count, never a loop: a bell that never stops moving gets
  // tuned out, and the design system keeps motion calm.
  const lastUnread = React.useRef(0);
  const [ringing, setRinging] = React.useState(false);
  React.useEffect(() => {
    if (unreadCount > lastUnread.current) setRinging(true);
    lastUnread.current = unreadCount;
  }, [unreadCount]);

  function openDropdown() {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) setCoords({ bottom: window.innerHeight - rect.bottom, left: rect.right + 10 });
    setOpen(true);
    readMutation.mutate();
  }

  function toggle() {
    if (open) setOpen(false);
    else openDropdown();
  }

  // Close on outside click + Escape.
  React.useEffect(() => {
    if (!open) return;
    function handlePointer(e: MouseEvent) {
      const target = e.target as Node;
      if (dropdownRef.current?.contains(target)) return;
      if (buttonRef.current?.contains(target)) return;
      setOpen(false);
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  function handleRowClick(n: NotificationItem) {
    setOpen(false);
    if (!n.link) return;
    if (/^https?:\/\//.test(n.link)) {
      window.open(n.link, "_blank", "noopener,noreferrer");
    } else {
      // Notification links are server-generated in-app paths (e.g. "/feed",
      // "/achievements") — not a literal from the route tree's known union,
      // so the typed navigator needs a loose cast here.
      void navigate({ to: n.link as "/feed" });
    }
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={toggle}
        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : "Notifications"}
        className={`atlas-bell${ringing ? " is-ringing" : ""}`}
        onAnimationEnd={(e) => {
          if (e.animationName === "atlas-bell-pulse") setRinging(false);
        }}
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 36,
          height: 36,
          marginBottom: 8,
          borderRadius: "var(--radius-md)",
          border: `1px solid ${unreadCount > 0 ? "var(--border-gold)" : "var(--border-subtle)"}`,
          background: open ? "var(--surface-hover)" : "var(--surface-card)",
          color: unreadCount > 0 ? "var(--gold-400)" : "var(--text-secondary)",
          cursor: "pointer",
          transition: "var(--t-colors)",
        }}
      >
        <Bell size={17} className="atlas-bell-icon" />
        {unreadCount > 0 && (
          <span
            aria-hidden="true"
            className="tnum"
            style={{
              position: "absolute",
              top: -6,
              right: -6,
              minWidth: 18,
              height: 18,
              padding: "0 5px",
              borderRadius: 9,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "var(--gold-400)",
              color: "var(--text-on-gold)",
              fontFamily: "var(--font-mono)",
              fontSize: 10.5,
              fontWeight: 700,
              lineHeight: 1,
              boxShadow: "0 0 0 2px var(--bg-app)",
            }}
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open &&
        coords &&
        createPortal(
          <div
            ref={dropdownRef}
            className="atlas-glass"
            style={{
              position: "fixed",
              bottom: coords.bottom,
              left: coords.left,
              zIndex: 1000,
              width: 340,
              maxHeight: 420,
              overflow: "auto",
              borderRadius: "var(--radius-lg)",
              boxShadow: "var(--shadow-lg)",
              padding: 8,
            }}
          >
            <p className="eyebrow" style={{ fontSize: 10, margin: "6px 10px" }}>
              Notifications
            </p>
            {items.length === 0 ? (
              <p style={{ fontSize: 12.5, color: "var(--text-faint)", margin: "8px 10px 12px" }}>
                You're all caught up.
              </p>
            ) : (
              items.map((n) => {
                const unread = !n.readAt;
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => handleRowClick(n)}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 8,
                      width: "100%",
                      padding: "9px 10px",
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      borderRadius: "var(--radius-sm)",
                      textAlign: "left",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "var(--surface-hover)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        width: 6,
                        height: 6,
                        marginTop: 6,
                        flexShrink: 0,
                        borderRadius: "50%",
                        background: unread ? "var(--gold-400)" : "transparent",
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: unread ? 700 : 500,
                          color: unread ? "var(--text-primary)" : "var(--text-secondary)",
                        }}
                      >
                        {n.title}
                      </div>
                      {n.body && (
                        <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2, lineHeight: 1.4 }}>
                          {n.body}
                        </div>
                      )}
                      <div
                        className="tnum"
                        style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, color: "var(--text-faint)", marginTop: 4 }}
                      >
                        {formatRelativeTime(n.createdAt)}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>,
          document.body,
        )}
    </>
  );
}
